import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { gigSwal } from '../utils/swal';
import io from 'socket.io-client';
import { Music, User, ListMusic, CheckCircle, Users, SlidersHorizontal, X } from 'lucide-react';
import { getDeviceId } from '../utils/deviceId';
import { resolveTheme } from '../constants/themes';
import SOCIAL_PLATFORMS from '../constants/socialIcons';
import useFavicon from '../hooks/useFavicon';
import useIsMobile from '../hooks/useIsMobile';

const API_URL = process.env.REACT_APP_API_URL || '';
// Header height when a band logo is present; logo is sized to 80% of this.
const HEADER_LOGO_AREA_HEIGHT = 110;
// Footer pill/icon geometry, shared by the rendered footer and its fit check.
const FOOTER_GAP = 8;
const FOOTER_ICON_SIZE = 32;
const FOOTER_SIDE_PADDING = 12;

function AudienceView() {
  const { bandSlug } = useParams();
  const location = useLocation();
  const cameFromHome = new URLSearchParams(location.search).get('ref') === 'home';
  const [songs, setSongs] = useState([]);
  const [songsCapped, setSongsCapped] = useState(false);
  // Remembered on this phone, so the thank-you still knows who they are after a reload
  const [requesterName, setRequesterName] = useState(() => {
    try { return localStorage.getItem('gr_requester_name') || ''; } catch { return ''; }
  });
  const changeRequesterName = (name) => {
    setRequesterName(name);
    try { localStorage.setItem('gr_requester_name', name); } catch {}
  };
  // 'songs' or 'leaderboard' (the songs requested so far, most votes first)
  const [view, setView] = useState('songs');
  const viewRef = useRef('songs');
  const [leaderboard, setLeaderboard] = useState(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [deviceLimit, setDeviceLimit] = useState(null);
  const [deviceId] = useState(getDeviceId());
  const [requestingId, setRequestingId] = useState(null);
  const [gigStatus, setGigStatus] = useState(null);
  const [branding, setBranding] = useState(null);
  const [members, setMembers] = useState([]);
  const [suggestionSheetOpen, setSuggestionSheetOpen] = useState(false);
  const [suggestionTitle, setSuggestionTitle] = useState('');
  const [suggestionArtist, setSuggestionArtist] = useState('');
  const [suggestionSent, setSuggestionSent] = useState(false);
  // Songs this phone has requested since the band last reset device limits (from the server)
  const [requestedSongIds, setRequestedSongIds] = useState([]);

  const [brandingLoaded, setBrandingLoaded] = useState(false);
  const [statusLoaded, setStatusLoaded] = useState(false);
  // No gig live: the QR on a printed request sheet gets scanned before the band starts
  const [noActiveGig, setNoActiveGig] = useState(false);
  const [nextGig, setNextGig] = useState(null);

  // Welcome modal
  const [showWelcome, setShowWelcome] = useState(!cameFromHome);

  // Post-gig survey
  const [showSurvey, setShowSurvey] = useState(false);
  const [surveyRating, setSurveyRating] = useState(0);
  const [surveyFavourite, setSurveyFavourite] = useState('');
  const [surveyLeastFavourite, setSurveyLeastFavourite] = useState('');
  const [surveySubmitting, setSurveySubmitting] = useState(false);
  const [surveyThanks, setSurveyThanks] = useState(false);
  const gigStatusRef = useRef(null);
  useEffect(() => { gigStatusRef.current = gigStatus; }, [gigStatus]);
  const deviceLimitRef = useRef(null);
  useEffect(() => { deviceLimitRef.current = deviceLimit; }, [deviceLimit]);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGenres, setSelectedGenres] = useState(['All']);
  const [filterOpen, setFilterOpen] = useState(false);

  // Bottom sheet drag-to-dismiss
  const [sheetY, setSheetY] = useState(0);
  const dragStart = useRef(null);
  const sheetRef = useRef(null);

  // Lock body scroll while filter sheet is open
  useEffect(() => {
    document.body.style.overflow = (filterOpen || suggestionSheetOpen) ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [filterOpen, suggestionSheetOpen]);

  useFavicon(branding?.logo_url);

  useEffect(() => {
    fetchSongs();
    checkDeviceLimit();
    checkGigStatus();
    axios.get(`${API_URL}/api/band/${bandSlug}/branding`)
      .then(res => { setBranding(res.data); setBrandingLoaded(true); })
      .catch(() => setBrandingLoaded(true));
    axios.get(`${API_URL}/api/band/${bandSlug}/members`).then(res => setMembers(res.data)).catch(() => {});

    const socket = io(API_URL);
    socket.emit('joinGig', bandSlug);

    socket.on('setListUpdated', () => {
      fetchSongs();
    });

    // Votes in, songs played or cleared: refresh the leaderboard if it's open
    const refreshLeaderboard = () => { if (viewRef.current === 'leaderboard') fetchLeaderboard(); };
    socket.on('newRequest', refreshLeaderboard);
    socket.on('requestsChanged', refreshLeaderboard);
    socket.on('requestsCleared', refreshLeaderboard);

    // The band reset everyone's limits or changed the limit: re-check this phone's
    socket.on('deviceLimitsChanged', async () => {
      const wasAtLimit = deviceLimitRef.current && !deviceLimitRef.current.canRequest;
      const now = await checkDeviceLimit();
      if (wasAtLimit && now?.canRequest) {
        flashMessage('Good news, you can request again!', 5000);
      }
    });

    // The band reset device limits: votes back and the full song list again
    socket.on('deviceLimitsReset', async () => {
      await checkDeviceLimit();
      flashMessage('The band have reset requests. Pick again!', 5000);
      if (viewRef.current === 'leaderboard') fetchLeaderboard();
    });

    socket.on('requestStatusChanged', (data) => {
      setGigStatus(prev => ({
        ...prev,
        is_accepting_requests: data.is_accepting_requests
      }));
      flashMessage(data.is_accepting_requests ? 'Requests are now open!' : 'Requests have been paused', 3000);
    });

    socket.on('gigEnded', () => {
      const gs = gigStatusRef.current;
      if (!gs || !gs.gig_id) return;
      const key = `gig_survey_${bandSlug}_${gs.gig_id}`;
      if (localStorage.getItem(key)) return;
      setShowSurvey(true);
    });

    return () => socket.disconnect();
  }, [bandSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkGigStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/band/${bandSlug}/status`);
      // Use a functional update so that if a socket requestStatusChanged event
      // arrived before this response, its value wins over the potentially-stale
      // HTTP snapshot (race: toggle fires between request sent and response received).
      setGigStatus(prev => ({
        ...response.data,
        is_accepting_requests: prev !== null
          ? prev.is_accepting_requests
          : response.data.is_accepting_requests,
      }));
      if (!cameFromHome) setShowWelcome(true);
      setStatusLoaded(true);
    } catch (err) {
      if (err.response?.status === 404) {
        // Nothing to request yet: the welcome screen is all there is, even coming from home
        setNoActiveGig(true);
        setNextGig(err.response.data?.next_gig || null);
        setShowWelcome(true);
      } else {
        console.error('Error checking gig status:', err);
      }
      setStatusLoaded(true);
    }
  };

  // While nothing is live, look again every 30 seconds and when the phone comes back to
  // the page; once the gig starts, reload so the socket joins the live gig's room
  useEffect(() => {
    if (!noActiveGig) return;
    const check = () => axios.get(`${API_URL}/api/band/${bandSlug}/status`)
      .then(() => window.location.reload())
      .catch(() => {});
    const timer = setInterval(check, 30000);
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', onVisible); };
  }, [noActiveGig, bandSlug]);

  const dismissWelcome = () => setShowWelcome(false);

  const formatWelcomeDate = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('en-GB', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    });
  };

  const fetchSongs = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/band/${bandSlug}/songs`);
      setSongs(response.data.songs);
      setSongsCapped(response.data.capped);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setMessage('Error loading songs');
      setLoading(false);
    }
  };

  // One clear timer for all transient messages, so an older message's timer can't wipe a newer one
  const messageTimer = useRef(null);
  const flashMessage = (text, ms) => {
    clearTimeout(messageTimer.current);
    setMessage(text);
    messageTimer.current = setTimeout(() => setMessage(''), ms);
  };

  const fetchLeaderboard = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/band/${bandSlug}/leaderboard`);
      setLeaderboard(response.data.songs);
    } catch (err) {
      console.error('Error loading leaderboard:', err);
    }
  };

  const showView = (v) => {
    viewRef.current = v;
    setView(v);
    if (v === 'leaderboard') fetchLeaderboard();
  };

  const checkDeviceLimit = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/band/${bandSlug}/can-request?deviceId=${deviceId}`
      );
      setDeviceLimit(response.data);
      if (Array.isArray(response.data.requested_song_ids)) setRequestedSongIds(response.data.requested_song_ids);
      return response.data;
    } catch (err) {
      console.error('Error checking device limit:', err);
    }
  };

  const handleRequest = async (songId) => {
    if (!requesterName.trim()) {
      gigSwal(theme, {
        title: 'Name Required',
        text: 'Please enter your name before requesting a song',
        icon: 'info',
        confirmButtonText: 'OK'
      });
      return;
    }

    if (gigStatus && !gigStatus.is_accepting_requests) {
      gigSwal(theme, {
        title: 'Requests are paused',
        text: 'The band have paused requests for now. Have a browse, and try again when they reopen.',
        icon: 'info',
        confirmButtonText: 'OK'
      });
      return;
    }

    if (deviceLimit && !deviceLimit.canRequest) {
      setMessage(`You've already requested ${deviceLimit.maxRequests} song${deviceLimit.maxRequests > 1 ? 's' : ''}!`);
      return;
    }

    setRequestingId(songId);

    try {
      await axios.post(`${API_URL}/api/band/${bandSlug}/requests`, {
        songId,
        requesterName,
        deviceId,
      });

      // Hide it straight away; the limit check then confirms this phone's list from the server
      setRequestedSongIds(prev => [...prev, songId]);
      await checkDeviceLimit();

      flashMessage('Request submitted!', 3000);
    } catch (err) {
      if (err.response && err.response.status === 403) {
        gigSwal(theme, {
          title: 'Requests are paused',
          text: 'The band have paused requests for now. Have a browse, and try again when they reopen.',
          icon: 'info',
          confirmButtonText: 'OK'
        });
        checkGigStatus();
      } else if (err.response && err.response.status === 429 && err.response.data?.limitReached) {
        setMessage(`You've already requested ${err.response.data.maxRequests} song${err.response.data.maxRequests > 1 ? 's' : ''}!`);
        checkDeviceLimit();
      } else if (err.response && err.response.status === 409) {
        flashMessage("You've already requested this song!", 3000);
      } else {
        setMessage('Error submitting request');
      }
    } finally {
      setRequestingId(null);
    }
  };

  const handleSuggest = async () => {
    if (!suggestionTitle.trim()) return;
    try {
      await axios.post(`${API_URL}/api/band/${bandSlug}/suggestions`, {
        title: suggestionTitle,
        artist_name: suggestionArtist,
        suggested_by: requesterName,
        deviceId, // lets the server rate-limit per device rather than per (shared venue) IP
      });
      setSuggestionSent(true);
      setSuggestionTitle('');
      setSuggestionArtist('');
      setTimeout(() => {
        setSuggestionSent(false);
        setSuggestionSheetOpen(false);
      }, 3000);
    } catch {
      // silent fail
    }
  };

  const skipSurvey = () => {
    const gs = gigStatusRef.current;
    if (gs?.gig_id) {
      try { localStorage.setItem(`gig_survey_${bandSlug}_${gs.gig_id}`, '1'); } catch {}
    }
    setShowSurvey(false);
  };

  const submitSurvey = async () => {
    if (!surveyRating) return;
    const gs = gigStatusRef.current;
    setSurveySubmitting(true);
    try {
      await axios.post(`${API_URL}/api/band/${bandSlug}/feedback`, {
        gigId: gs.gig_id,
        deviceId,
        favouriteSong: surveyFavourite,
        leastFavouriteSong: surveyLeastFavourite,
        rating: surveyRating,
      });
      setSurveyThanks(true);
      setTimeout(() => setShowSurvey(false), 1800);
    } catch {
      setShowSurvey(false);
    } finally {
      try { localStorage.setItem(`gig_survey_${bandSlug}_${gs.gig_id}`, '1'); } catch {}
      setSurveySubmitting(false);
    }
  };

  // Genre filter helpers
  const toggleGenre = (g) => {
    if (g === 'All') { setSelectedGenres(['All']); return; }
    setSelectedGenres(prev => {
      const wo = prev.filter(x => x !== 'All');
      if (wo.includes(g)) {
        const next = wo.filter(x => x !== g);
        return next.length === 0 ? ['All'] : next;
      }
      return [...wo, g];
    });
  };

  // Sheet drag handlers
  const onTouchStart = e => { dragStart.current = e.touches[0].clientY; };
  const onTouchMove = e => {
    const dy = e.touches[0].clientY - dragStart.current;
    if (dy > 0) setSheetY(dy);
  };
  const onTouchEnd = () => {
    if (sheetY > 80) setFilterOpen(false);
    setSheetY(0);
  };

  const uniqueGenres = [...new Set(songs.map(song => song.genre))].sort();
  const isAll = selectedGenres.includes('All');
  const filterLabel = isAll ? null : selectedGenres.join(' · ');

  const filteredSongs = songs.filter(song => {
    if (requestedSongIds.includes(song.id)) return false;
    const gOk = isAll || selectedGenres.includes(song.genre);
    const matchesSearch =
      song.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      song.artist_original.toLowerCase().includes(searchTerm.toLowerCase());
    return gOk && matchesSearch;
  });

  const hasReachedLimit = deviceLimit && !deviceLimit.canRequest;

  const theme = resolveTheme(branding || {});
  const primary = theme.primary;
  const headerBg = theme.navGradient;

  // Brand header identity
  const brandName = branding?.band_name || 'GigRequest';
  const brandFontSize =
    brandName.length <= 12 ? 28 :
    brandName.length <= 18 ? 22 :
    brandName.length <= 26 ? 17 : 14;

  const activeSocials = branding?.show_social_media
    ? SOCIAL_PLATFORMS.filter(p => branding?.[p.key])
    : [];
  const activeSocialKeys = activeSocials.map(p => p.key).join(',');

  // Desktop/tablet: the phone-width column is shown as a card over a backdrop.
  const isWide = !useIsMobile(768);

  // Footer: "Meet the Band" and "Suggest a song" are always visible. Socials
  // show as labelled pills when everything fits on one line, otherwise as
  // icon-only buttons, which drop to their own row if they still don't fit.
  const [footerMode, setFooterMode] = useState('labels'); // 'labels' | 'icons' | 'icons-row'
  const footerRowRef = useRef(null);
  const footerPinnedRefs = useRef([]);
  const footerLabelMeasureRef = useRef(null);
  useLayoutEffect(() => {
    const row = footerRowRef.current;
    const measure = footerLabelMeasureRef.current;
    if (!row || !measure) return;
    const count = activeSocialKeys ? activeSocialKeys.split(',').length : 0;
    const update = () => {
      if (count === 0) { setFooterMode('labels'); return; }
      const available = row.clientWidth - FOOTER_SIDE_PADDING * 2;
      const pinned = footerPinnedRefs.current.filter(Boolean);
      const pinnedWidth = pinned.reduce((sum, el) => sum + el.offsetWidth, 0);
      const gapsWith = (n) => (pinned.length + n - 1) * FOOTER_GAP;
      const labelsWidth = pinnedWidth + measure.offsetWidth + gapsWith(count);
      const iconsWidth = pinnedWidth + count * FOOTER_ICON_SIZE + gapsWith(count);
      setFooterMode(
        labelsWidth <= available ? 'labels' :
        iconsWidth <= available ? 'icons' : 'icons-row'
      );
    };
    update();
    // Re-check on resize, and when web fonts load and change the pill widths.
    const observer = new ResizeObserver(update);
    observer.observe(row);
    observer.observe(measure);
    return () => observer.disconnect();
  }, [activeSocialKeys, members.length]);

  const footerPillStyle = {
    display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
    padding: '0 12px', borderRadius: '999px', height: FOOTER_ICON_SIZE,
    border: `1px solid ${primary}`, color: primary,
    background: theme.surface, textDecoration: 'none',
    fontSize: '0.8rem', fontWeight: '600', fontFamily: 'inherit',
    boxSizing: 'border-box', whiteSpace: 'nowrap', flexShrink: 0,
  };
  const footerIconStyle = {
    ...footerPillStyle,
    padding: 0, width: FOOTER_ICON_SIZE, justifyContent: 'center',
  };
  const renderSocialLinks = (iconsOnly) => activeSocials.map(({ key, label, Icon }) => (
    <a
      key={key}
      href={branding[key]}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={iconsOnly ? label : undefined}
      title={iconsOnly ? label : undefined}
      style={iconsOnly ? footerIconStyle : footerPillStyle}
    >
      <Icon size={iconsOnly ? 15 : 13} />{!iconsOnly && <> {label}</>}
    </a>
  ));
  const backdropImage = branding?.splash_bg_url || branding?.promo_photo_url;

  const splashReady = brandingLoaded && statusLoaded && !loading;

  const renderLeaderboard = () => {
    if (leaderboard === null) {
      return <p style={{ textAlign: 'center', color: theme.textMuted, padding: '2rem 0' }}>Loading…</p>;
    }
    if (leaderboard.length === 0) {
      return (
        <div style={{ padding: '2.5rem 1rem', textAlign: 'center', background: theme.surface, borderRadius: 12, color: theme.textSecondary }}>
          <div style={{ fontSize: 32, marginBottom: '0.5rem' }}>🏆</div>
          <p style={{ margin: '0 0 1rem' }}>No requests yet{hasReachedLimit ? '.' : ', so yours could be top!'}</p>
          {!hasReachedLimit && (
            <button onClick={() => showView('songs')} style={{ background: primary, color: theme.onPrimary, border: 'none', borderRadius: 10, padding: '10px 18px', fontWeight: 700, fontSize: 14, cursor: 'pointer', minHeight: 44, fontFamily: 'inherit' }}>
              Pick a song
            </button>
          )}
        </div>
      );
    }
    return (
      <>
        <p style={{ color: theme.textMuted, fontSize: 13, margin: '0 4px 10px' }}>
          The most-requested songs so far. Played songs drop off the list.
        </p>
        <div style={{ display: 'grid', gap: 8 }}>
          {leaderboard.map((song, i) => {
            const mine = requestedSongIds.includes(song.song_id);
            return (
              <div key={song.song_id} style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
                background: theme.surface, borderRadius: 10,
                border: mine ? `2px solid ${primary}` : '2px solid transparent',
                boxShadow: theme.isDark ? 'none' : '0 1px 3px rgba(0,0,0,0.08)',
              }}>
                <div style={{
                  width: 32, flexShrink: 0, textAlign: 'center', fontWeight: 800,
                  fontSize: i < 3 ? 22 : 16, color: theme.textMuted,
                }}>
                  {['🥇', '🥈', '🥉'][i] || i + 1}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {song.title}
                  </div>
                  <div style={{ color: theme.textSecondary, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {song.artist_original}
                    {mine && <span style={{ color: primary, fontWeight: 700 }}> · Your pick</span>}
                  </div>
                </div>
                <div style={{ flexShrink: 0, textAlign: 'center', minWidth: 44 }}>
                  <div style={{ fontWeight: 800, fontSize: 18, color: primary, lineHeight: 1 }}>{song.votes}</div>
                  <div style={{ fontSize: 11, color: theme.textMuted }}>{song.votes === 1 ? 'vote' : 'votes'}</div>
                </div>
              </div>
            );
          })}
        </div>
      </>
    );
  };

  return (
    <>
      {/* Welcome modal */}
      {showWelcome && (() => {
        const hasSplash = !!branding?.splash_bg_url;
        const textColor = hasSplash ? '#fff' : theme.text;
        const subColor  = hasSplash ? 'rgba(255,255,255,0.8)' : theme.textSecondary;

        const overlayBg = hasSplash ? '#000'
          : theme.isDark
            ? `linear-gradient(160deg, ${theme.background} 0%, ${theme.surface} 100%)`
            : theme.surfaceAlt;

        if (!splashReady) {
          return (
            <div style={{
              position: 'fixed', inset: 0, zIndex: 1000,
              background: '#000',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <style>{`@keyframes gr-spin { to { transform: rotate(360deg); } }`}</style>
              <div style={{
                width: 36, height: 36, borderRadius: '50%',
                border: '3px solid rgba(255,255,255,0.12)',
                borderTopColor: 'rgba(255,255,255,0.65)',
                animation: 'gr-spin 0.75s linear infinite',
              }} />
            </div>
          );
        }

        return (
          <div style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: overlayBg,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '1.5rem',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            overflow: 'hidden',
          }}>
            <style>{`@keyframes gr-fade { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }`}</style>
            {hasSplash && (
              <>
                <img
                  src={branding.splash_bg_url}
                  alt=""
                  style={{
                    position: 'absolute', inset: 0,
                    width: '100%', height: '100%',
                    objectFit: 'cover',
                    objectPosition: `${branding.splash_bg_x ?? 50}% ${branding.splash_bg_y ?? 50}%`,
                    transform: `scale(${(branding.splash_bg_zoom || 100) / 100})`,
                    transformOrigin: 'center center',
                    pointerEvents: 'none',
                  }}
                />
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.52)' }} />
              </>
            )}
            {branding?.logo_url && (
              <img
                src={branding.logo_url}
                alt="Band logo"
                style={{
                  position: 'absolute',
                  top: 'max(28px, calc(28px + env(safe-area-inset-top)))',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 72, height: 72,
                  objectFit: 'contain', borderRadius: 12,
                  zIndex: 2,
                }}
              />
            )}

            <div style={{ maxWidth: '480px', width: '100%', position: 'relative', zIndex: 1, animation: 'gr-fade 0.35s ease' }}>
              <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
                {!branding?.logo_url && (
                  <div style={{ marginBottom: '0.5rem' }}><Music size={48} color={hasSplash ? '#fff' : theme.primary} /></div>
                )}
                {(branding?.band_name || gigStatus?.gig_name) && (
                  <h1 style={{ color: textColor, fontSize: '2.2rem', fontWeight: '700', margin: '0 0 0.25rem', lineHeight: 1.2, textShadow: hasSplash ? '0 2px 8px rgba(0,0,0,0.5)' : 'none' }}>
                    {branding?.band_name || gigStatus?.gig_name}
                  </h1>
                )}
                {branding?.tagline && (
                  <p style={{ color: subColor, margin: '0 0 0.5rem', fontSize: '1rem', fontStyle: 'italic' }}>
                    {branding.tagline}
                  </p>
                )}
                {(gigStatus?.venue_name || gigStatus?.gig_date) && (
                  <p style={{ color: subColor, margin: '0 0 1rem', fontSize: '0.95rem' }}>
                    {[gigStatus.venue_name, gigStatus.gig_date && formatWelcomeDate(gigStatus.gig_date)].filter(Boolean).join('  ·  ')}
                  </p>
                )}
                {noActiveGig && nextGig && (
                  <p style={{ color: subColor, margin: '0 0 1rem', fontSize: '0.95rem' }}>
                    <span style={{ fontWeight: '600', color: textColor }}>Next gig:</span>{' '}
                    {[nextGig.venue_name || nextGig.gig_name, formatWelcomeDate(nextGig.gig_date), nextGig.gig_time && String(nextGig.gig_time).slice(0, 5)].filter(Boolean).join('  ·  ')}
                  </p>
                )}
                {(gigStatus || noActiveGig) && (() => {
                  // Green: open. Red: live but paused. Grey: no gig live yet.
                  const [dot, label] = noActiveGig ? ['#9ca3af', 'Not live yet']
                    : gigStatus.is_accepting_requests ? ['#34d399', 'Requests are open']
                    : ['#f87171', 'Requests are paused'];
                  return (
                    <span style={{
                      display: 'inline-block',
                      background: `${dot}26`,
                      border: `1px solid ${dot}66`,
                      color: noActiveGig && !hasSplash ? theme.textSecondary : dot,
                      borderRadius: '999px',
                      padding: '0.25rem 0.9rem',
                      fontSize: '0.85rem',
                      fontWeight: '600',
                    }}>
                      <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: dot, marginRight: '0.4rem', verticalAlign: 'middle' }} />
                      {label}
                    </span>
                  );
                })()}
              </div>

              <div style={{
                background: hasSplash ? 'rgba(0,0,0,0.35)' : theme.isDark ? 'rgba(255,255,255,0.06)' : theme.surface,
                border: `1px solid ${hasSplash ? 'rgba(255,255,255,0.15)' : theme.isDark ? 'rgba(255,255,255,0.1)' : theme.border}`,
                borderRadius: '16px',
                padding: '1.75rem',
                marginBottom: '1.75rem',
                backdropFilter: hasSplash ? 'blur(12px)' : 'none',
                WebkitBackdropFilter: hasSplash ? 'blur(12px)' : 'none',
              }}>
                <p style={{
                  color: theme.textMuted, fontSize: '0.75rem', fontWeight: '700',
                  letterSpacing: '0.1em', textTransform: 'uppercase',
                  margin: '0 0 1.25rem', textAlign: 'center',
                }}>
                  How it works
                </p>
                {[
                  [User,        'Enter your name',       'So we know who to shout out!'],
                  [ListMusic,   'Browse the song list',  'See everything we can play tonight'],
                  [CheckCircle, 'Tap a song to request it', "We'll do our best to play it!"],
                ].map(([StepIcon, title, sub], i) => (
                  <div key={i} style={{ display: 'flex', gap: '1rem', marginBottom: i < 2 ? '1rem' : 0 }}>
                    <div style={{
                      width: '36px', height: '36px', borderRadius: '10px',
                      background: hasSplash ? 'rgba(255,255,255,0.15)' : `${theme.primary}20`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <StepIcon size={18} color={hasSplash ? '#fff' : theme.primary} />
                    </div>
                    <div>
                      <div style={{ color: textColor, fontWeight: '600', fontSize: '0.95rem' }}>{title}</div>
                      <div style={{ color: subColor, fontSize: '0.82rem', marginTop: '0.1rem' }}>{sub}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* The button only goes through to the songs once a gig is live; the page
                  switches itself on when it is (see the noActiveGig effect) */}
              <button
                onClick={dismissWelcome}
                disabled={noActiveGig}
                style={{
                  display: 'block', width: '100%',
                  padding: '1.1rem',
                  background: noActiveGig ? (hasSplash ? 'rgba(255,255,255,0.12)' : theme.border) : headerBg,
                  color: noActiveGig ? subColor : theme.onPrimary,
                  border: 'none', borderRadius: '12px',
                  fontSize: noActiveGig ? '0.95rem' : '1.1rem', fontWeight: '700',
                  cursor: noActiveGig ? 'default' : 'pointer',
                  boxShadow: noActiveGig ? 'none' : `0 4px 24px ${theme.primary}66`,
                  minHeight: 52,
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                {noActiveGig ? 'Requests open when the band start playing'
                  : gigStatus && !gigStatus.is_accepting_requests ? 'Browse the songs' : "Let's go!"}
              </button>
              {noActiveGig && (
                <a href={`/band/${bandSlug}`} style={{ display: 'block', textAlign: 'center', marginTop: '1rem', color: textColor, fontSize: '0.9rem', fontWeight: '600' }}>
                  {branding?.band_name ? `More about ${branding.band_name}` : 'More about the band'}
                </a>
              )}
            </div>
          </div>
        );
      })()}

      {showSurvey && (
        <PostGigSurveyModal
          theme={theme}
          bandName={branding?.band_name || gigStatus?.gig_name}
          venueName={gigStatus?.venue_name}
          rating={surveyRating}
          setRating={setSurveyRating}
          favourite={surveyFavourite}
          setFavourite={setSurveyFavourite}
          leastFavourite={surveyLeastFavourite}
          setLeastFavourite={setSurveyLeastFavourite}
          onSubmit={submitSurvey}
          onSkip={skipSurvey}
          submitting={surveySubmitting}
          thanks={surveyThanks}
        />
      )}

      <style>{`
        .gr-input::placeholder { color: ${theme.textMuted}; opacity: 1; }
        .gr-input::-webkit-input-placeholder { color: ${theme.textMuted}; }
        .gr-input::-moz-placeholder { color: ${theme.textMuted}; }
      `}</style>

      {/* Desktop/tablet backdrop behind the card: the band's own imagery, else its header gradient */}
      {isWide && (
        <div aria-hidden="true" style={{ position: 'fixed', inset: 0, zIndex: 0, overflow: 'hidden', background: headerBg }}>
          {backdropImage && (
            <img
              src={backdropImage}
              alt=""
              style={{
                position: 'absolute', inset: 0, width: '100%', height: '100%',
                objectFit: 'cover', filter: 'blur(24px)', transform: 'scale(1.1)',
              }}
            />
          )}
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)' }} />
        </div>
      )}

      <div style={{
        position: 'relative',
        zIndex: 1,
        height: '100dvh',
        padding: isWide ? '24px 16px' : 0,
        boxSizing: 'border-box',
      }}>
      <div style={{
        maxWidth: isWide ? 720 : 600,
        margin: '0 auto',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        background: theme.background,
        color: theme.text,
        WebkitFontSmoothing: 'antialiased',
        borderRadius: isWide ? 16 : 0,
        boxShadow: isWide ? '0 20px 60px rgba(0,0,0,0.45)' : 'none',
      }}>

        {/* ── BRAND HEADER ─────────────────────────────────── */}
        <div style={{
          flexShrink: 0,
          position: 'relative',
          overflow: 'hidden',
          background: headerBg,
          padding: '14px 16px 12px',
          paddingTop: 'max(14px, calc(14px + env(safe-area-inset-top)))',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          minHeight: branding?.logo_url ? HEADER_LOGO_AREA_HEIGHT : (branding?.promo_photo_url ? 68 : undefined),
        }}>
          {branding?.promo_photo_url && (
            <>
              <img
                src={branding.promo_photo_url}
                alt=""
                style={{
                  position: 'absolute', inset: 0,
                  width: '100%', height: '100%',
                  objectFit: 'cover',
                  objectPosition: `${branding.header_bg_x ?? 50}% ${branding.header_bg_y ?? 50}%`,
                  pointerEvents: 'none',
                  zIndex: 0,
                }}
              />
              <div style={{ position: 'absolute', inset: 0, background: headerBg, opacity: 0.78, zIndex: 1 }} />
            </>
          )}
          {/* Logo pinned to the left, vertically centered in the header */}
          {branding?.logo_url && (
            <img
              src={branding.logo_url}
              alt={brandName}
              style={{
                position: 'absolute',
                top: '50%',
                left: 16,
                transform: 'translateY(-50%)',
                height: HEADER_LOGO_AREA_HEIGHT * 0.8,
                maxWidth: 240,
                objectFit: 'contain',
                zIndex: 2,
              }}
            />
          )}

          <div style={{ display: 'flex', flex: 1, alignItems: 'center', justifyContent: branding?.logo_url ? 'flex-end' : 'space-between', gap: 12, position: 'relative', zIndex: 2 }}>
            {/* Band name — shown only when there is no logo */}
            {!branding?.logo_url && (
              <div>
                <div style={{
                  fontSize: 10, letterSpacing: 3, textTransform: 'uppercase',
                  fontFamily: 'monospace', color: `${theme.onPrimary}80`,
                  lineHeight: 1, marginBottom: 2,
                }}>
                  Live Set
                </div>
                <div style={{
                  fontSize: brandFontSize,
                  fontWeight: 900,
                  color: theme.onPrimary,
                  lineHeight: 1,
                  letterSpacing: brandName.length > 18 ? 0 : -0.5,
                }}>
                  {brandName}
                </div>
              </div>
            )}

            {/* Requests remaining counter */}
            {deviceLimit && !hasReachedLimit && (
              <div style={{
                background: 'rgba(0,0,0,0.50)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: 22,
                padding: '6px 14px',
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                flexShrink: 0,
              }}>
                <Music size={14} color="#fff" />
                <span style={{
                  color: '#fff',
                  fontSize: 13,
                  fontFamily: 'monospace',
                  whiteSpace: 'nowrap',
                }}>
                  {deviceLimit.remaining} left
                </span>
              </div>
            )}
          </div>
        </div>

        {/* ── CONTROLS BAR ─────────────────────────────────── */}
        <div style={{
          flexShrink: 0,
          background: theme.isDark ? 'rgba(0,0,0,0.4)' : theme.surfaceAlt,
          padding: '10px 12px',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          borderBottom: `1px solid ${theme.border}`,
          zIndex: 100,
          boxShadow: theme.isDark ? 'none' : '0 2px 8px rgba(0,0,0,0.07)',
        }}>
          {/* Songs / leaderboard switch */}
          <div role="tablist" style={{ display: 'flex', background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 10, padding: 3, gap: 3 }}>
            {[['songs', 'Songs'], ['leaderboard', '🏆 Top requests']].map(([v, label]) => (
              <button
                key={v}
                role="tab"
                aria-selected={view === v}
                onClick={() => showView(v)}
                style={{
                  flex: 1, minHeight: 40, border: 'none', borderRadius: 8, cursor: 'pointer',
                  background: view === v ? primary : 'transparent',
                  color: view === v ? theme.onPrimary : theme.textSecondary,
                  fontWeight: 700, fontSize: 14, fontFamily: 'inherit',
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                {label}
              </button>
            ))}
          </div>

          {view === 'songs' && (<>
          {/* Name — full width, font-size 16 prevents iOS auto-zoom */}
          <input
            type="text"
            placeholder="Your name"
            value={requesterName}
            maxLength={100}
            onChange={(e) => changeRequesterName(e.target.value)}
            disabled={hasReachedLimit}
            className="gr-input"
            style={{
              width: '100%',
              boxSizing: 'border-box',
              background: theme.surface,
              border: `1px solid ${theme.border}`,
              borderRadius: 10,
              padding: '12px 14px',
              color: theme.text,
              fontSize: 16,
              outline: 'none',
              minHeight: 44,
            }}
          />

          {/* Search + filter button (nothing to search once the limit's reached) */}
          <div style={{ display: hasReachedLimit ? 'none' : 'flex', gap: 8 }}>
            <input
              type="text"
              placeholder="Search songs or artists..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="gr-input"
              style={{
                flex: 1,
                background: theme.surface,
                border: `1px solid ${theme.border}`,
                borderRadius: 10,
                padding: '0 14px',
                color: theme.text,
                fontSize: 16,
                outline: 'none',
                height: 50,
                boxSizing: 'border-box',
              }}
            />

            <button
              onClick={() => setFilterOpen(true)}
              style={{
                flexShrink: 0,
                background: filterLabel ? primary : theme.surface,
                border: filterLabel ? 'none' : `1px solid ${theme.border}`,
                borderRadius: 10,
                width: 50,
                height: 50,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                position: 'relative',
                WebkitTapHighlightColor: 'transparent',
                transition: 'background 0.15s',
                minHeight: 44,
              }}
            >
              <SlidersHorizontal
                size={20}
                color={filterLabel ? theme.onPrimary : theme.textSecondary}
              />
              {filterLabel && (
                <div style={{
                  position: 'absolute',
                  top: 8, right: 8,
                  width: 7, height: 7,
                  borderRadius: '50%',
                  background: theme.onPrimary,
                  opacity: 0.7,
                }} />
              )}
            </button>
          </div>

          {/* Active genre chips */}
          {filterLabel && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              {selectedGenres.map(g => (
                <span
                  key={g}
                  onClick={() => toggleGenre(g)}
                  style={{
                    background: `${primary}18`,
                    border: `1px solid ${primary}55`,
                    color: primary,
                    borderRadius: 14,
                    padding: '4px 10px',
                    fontSize: 12,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  {g} <X size={11} style={{ opacity: 0.6 }} />
                </span>
              ))}
              <span
                onClick={() => setSelectedGenres(['All'])}
                style={{ color: theme.textMuted, fontSize: 12, cursor: 'pointer' }}
              >
                Clear
              </span>
            </div>
          )}

          </>)}

          {/* Message feedback */}
          {message && (
            <div style={{
              fontSize: 12,
              color: theme.textSecondary,
              textAlign: 'center',
              background: theme.surface,
              border: `1px solid ${theme.border}`,
              padding: '6px',
              borderRadius: 6,
            }}>
              {message}
            </div>
          )}
        </div>

        {/* ── SONG LIST ────────────────────────────────────── */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          padding: '12px',
        }}>
          {/* Requests Closed Banner */}
          {view === 'songs' && gigStatus && !gigStatus.is_accepting_requests && !hasReachedLimit && (
            <div style={{ borderRadius: '12px', overflow: 'hidden', marginBottom: '1rem', border: '2px solid #dc3545' }}>
              <div style={{ background: '#f8d7da', padding: '0.9rem 1rem', textAlign: 'center' }}>
                <div style={{ color: '#721c24', fontWeight: '700', fontSize: '1rem', marginBottom: '0.2rem' }}>
                  <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#f87171', marginRight: '0.4rem', verticalAlign: 'middle' }} />
                  Requests are paused
                </div>
                <div style={{ color: '#721c24', fontSize: '0.85rem' }}>
                  Have a browse. You can request again when the band reopen requests.
                </div>
              </div>
              {(activeSocials.length > 0 || members.length > 0) && (
                <div style={{ background: theme.surface, padding: '1rem', textAlign: 'center', borderTop: '1px solid #f5c6cb' }}>
                  <p style={{ margin: '0 0 0.75rem', fontSize: '0.82rem', color: theme.textMuted, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    While you wait
                  </p>
                  <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '0.4rem' }}>
                    {members.length > 0 && (
                      <a
                        href={`/gig/${bandSlug}/about`}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                          padding: '0.35rem 0.85rem', borderRadius: '999px',
                          border: `1px solid ${primary}`, color: primary,
                          background: theme.surface, textDecoration: 'none',
                          fontSize: '0.8rem', fontWeight: '600', minHeight: 36,
                        }}
                      >
                        <Users size={14} /> Meet the Band
                      </a>
                    )}
                    {activeSocials.map(({ key, label, Icon }) => (
                      <a
                        key={key}
                        href={branding[key]}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                          padding: '0.35rem 0.85rem', borderRadius: '999px',
                          border: `1px solid ${primary}`, color: primary,
                          background: theme.surface, textDecoration: 'none',
                          fontSize: '0.8rem', fontWeight: '600', minHeight: 36,
                        }}
                      >
                        <Icon size={13} /> {label}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {view === 'leaderboard' ? renderLeaderboard() : hasReachedLimit ? (
            <div style={{
              padding: '2rem 1.5rem',
              background: theme.surface,
              border: `2px solid ${theme.border}`,
              borderRadius: '12px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: 36, marginBottom: '0.5rem' }}>🎉</div>
              <h3 style={{ margin: '0 0 0.75rem 0', color: theme.text }}>
                Thanks for voting{requesterName.trim() ? `, ${requesterName.trim()}` : ''}!
              </h3>
              <p style={{ margin: '0 0 1.25rem', color: theme.textSecondary, lineHeight: 1.5 }}>
                You've used all {deviceLimit.maxRequests === 1 ? 'your request' : `${deviceLimit.maxRequests} of your requests`}. See how your {deviceLimit.maxRequests === 1 ? 'pick is' : 'picks are'} doing on the leaderboard.
              </p>
              <button
                onClick={() => showView('leaderboard')}
                style={{
                  background: primary, color: theme.onPrimary, border: 'none', borderRadius: 10,
                  padding: '12px 22px', fontWeight: 700, fontSize: 15, cursor: 'pointer', minHeight: 44,
                  fontFamily: 'inherit', WebkitTapHighlightColor: 'transparent',
                }}
              >
                🏆 See the leaderboard
              </button>
            </div>
          ) : (
            <>
              {(searchTerm || !isAll) && (
                <p style={{ color: theme.textMuted, fontSize: 13, marginBottom: 12, padding: '0 4px' }}>
                  Showing {filteredSongs.length} of {songs.length} songs
                </p>
              )}

              {filteredSongs.length === 0 ? (
                <div style={{
                  padding: '3rem 1rem',
                  textAlign: 'center',
                  background: theme.surface,
                  borderRadius: '12px',
                  color: theme.textSecondary,
                }}>
                  <p>No songs found. Try a different search or filter.</p>
                </div>
              ) : (() => {
                const grid = (list) => (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isWide ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0, 1fr)',
                  gap: 8,
                }}>
                  {list.map(song => {
                    const closed = gigStatus && !gigStatus.is_accepting_requests;
                    const busy = requestingId === song.id;
                    return (
                      <div key={song.id} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '14px',
                        background: theme.surface,
                        borderRadius: 10,
                        boxShadow: theme.isDark ? 'none' : '0 1px 3px rgba(0,0,0,0.08)',
                        gap: 10,
                      }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            fontWeight: 600,
                            fontSize: 15,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            marginBottom: 3,
                          }}>
                            {song.rusty && <span title="Haven't played this for a while" style={{ marginRight: 6 }}>🤞</span>}
                            {song.title}
                          </div>
                          <div style={{ color: theme.textSecondary, fontSize: 13 }}>
                            {song.artist_original}
                            <span style={{ color: theme.textMuted, margin: '0 5px' }}>·</span>
                            <span style={{ textTransform: 'uppercase', letterSpacing: 0.4, fontSize: 11, color: theme.textMuted }}>
                              {song.genre}
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={() => handleRequest(song.id)}
                          disabled={busy || closed}
                          style={{
                            flexShrink: 0,
                            background: (busy || closed) ? 'transparent' : primary,
                            color: (busy || closed) ? theme.textMuted : theme.onPrimary,
                            border: (busy || closed) ? `1px solid ${theme.border}` : 'none',
                            borderRadius: 10,
                            padding: '10px 18px',
                            fontWeight: 700,
                            fontSize: 14,
                            cursor: (busy || closed) ? 'default' : 'pointer',
                            minHeight: 44,
                            WebkitTapHighlightColor: 'transparent',
                            transition: 'all 0.2s',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {busy ? '...' : closed ? 'Closed' : 'Request'}
                        </button>
                      </div>
                    );
                  })}
                </div>
                );
                // Rusty songs (Relaxed mode) come after the gig-ready ones, in their own labelled section
                const ready = filteredSongs.filter(song => !song.rusty);
                const rusty = filteredSongs.filter(song => song.rusty);
                return (
                  <>
                    {ready.length > 0 && grid(ready)}
                    {rusty.length > 0 && (
                      <>
                        <p style={{ color: theme.textSecondary, fontSize: 14, fontWeight: 600, margin: ready.length ? '20px 4px 10px' : '0 4px 10px' }}>
                          🤞 Haven't played these for a while, might be a bit rough!
                        </p>
                        {grid(rusty)}
                      </>
                    )}
                  </>
                );
              })()}

              {songsCapped && (
                <div style={{
                  marginTop: 16, padding: '10px 14px',
                  background: theme.surfaceAlt,
                  border: `1px solid ${theme.border}`,
                  borderRadius: 10,
                  textAlign: 'center',
                  fontSize: 13,
                  color: theme.textMuted,
                }}>
                  Showing {songs.length} songs tonight
                </div>
              )}
            </>
          )}

        </div>

        {/* ── FOOTER ───────────────────────────────────────── */}
        <div style={{
          flexShrink: 0,
          background: theme.surface,
          borderTop: `3px solid ${primary}`,
          boxShadow: theme.isDark ? 'none' : '0 -2px 8px rgba(0,0,0,0.08)',
          paddingBottom: 'max(6px, calc(6px + env(safe-area-inset-bottom)))',
        }}>
          <div ref={footerRowRef} style={{ position: 'relative' }}>
            {/* Hidden copy of the labelled social pills, used only to measure whether they fit */}
            <div
              ref={footerLabelMeasureRef}
              aria-hidden="true"
              style={{
                position: 'absolute', top: 0, left: 0, visibility: 'hidden', pointerEvents: 'none',
                display: 'inline-flex', gap: FOOTER_GAP, whiteSpace: 'nowrap',
              }}
            >
              {activeSocials.map(({ key, label, Icon }) => (
                <span key={key} style={footerPillStyle}><Icon size={13} /> {label}</span>
              ))}
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: FOOTER_GAP,
              padding: `8px ${FOOTER_SIDE_PADDING}px 4px`,
              boxSizing: 'border-box',
            }}>
              {members.length > 0 && (
                <a
                  ref={el => { footerPinnedRefs.current[0] = el; }}
                  href={`/gig/${bandSlug}/about`}
                  style={{ ...footerPillStyle, gap: '0.3rem' }}
                >
                  <Users size={13} /> Meet the Band
                </a>
              )}
              {footerMode !== 'icons-row' && renderSocialLinks(footerMode === 'icons')}
              <button
                ref={el => { footerPinnedRefs.current[1] = el; }}
                onClick={() => setSuggestionSheetOpen(true)}
                style={{ ...footerPillStyle, cursor: 'pointer', WebkitTapHighlightColor: 'transparent' }}
              >
                <Music size={13} /> Suggest a song
              </button>
            </div>
            {footerMode === 'icons-row' && (
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: FOOTER_GAP,
                padding: `4px ${FOOTER_SIDE_PADDING}px 4px`,
              }}>
                {renderSocialLinks(true)}
              </div>
            )}
          </div>
          {branding?.footer_text && (
            <p style={{ margin: '0 0.75rem 2px', fontSize: '0.72rem', color: theme.textMuted, textAlign: 'center' }}>
              {branding.footer_text}
            </p>
          )}
        </div>
      </div>
      </div>

      {/* ── SUGGESTION BOTTOM SHEET ──────────────────────── */}
      {suggestionSheetOpen && (
        <>
          <div
            onClick={() => setSuggestionSheetOpen(false)}
            style={{
              position: 'fixed', inset: 0,
              background: 'rgba(0,0,0,0.6)',
              zIndex: 200,
              WebkitTapHighlightColor: 'transparent',
            }}
          />
          <div
            style={{
              position: 'fixed',
              bottom: 0, left: 0, right: 0,
              zIndex: 201,
              background: theme.surface,
              borderTop: `2px solid ${primary}`,
              borderRadius: '20px 20px 0 0',
              padding: `0 16px calc(24px + env(safe-area-inset-bottom))`,
              boxShadow: '0 -8px 40px rgba(0,0,0,0.3)',
              maxWidth: 600,
              margin: '0 auto',
            }}
          >
            <div style={{
              width: 40, height: 4,
              background: theme.border,
              borderRadius: 2,
              margin: '12px auto 20px',
            }} />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div>
                <div style={{ color: theme.text, fontWeight: 700, fontSize: 18 }}>Suggest a Song</div>
                <div style={{ color: theme.textMuted, fontSize: 12, marginTop: 3 }}>Can't find it? We'll consider it for future sets</div>
              </div>
              <button
                onClick={() => setSuggestionSheetOpen(false)}
                style={{
                  background: theme.surfaceAlt, border: 'none',
                  color: theme.textSecondary, borderRadius: '50%',
                  width: 34, height: 34,
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                <X size={18} />
              </button>
            </div>

            {suggestionSent ? (
              <div style={{ textAlign: 'center', padding: '1.5rem', background: '#d4edda', borderRadius: '12px', color: '#155724', fontWeight: '600', fontSize: '0.95rem' }}>
                Thanks! We'll consider it for future sets.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <input
                  type="text"
                  placeholder="Song title *"
                  value={suggestionTitle}
                  maxLength={200}
                  onChange={e => setSuggestionTitle(e.target.value)}
                  className="gr-input"
                  style={{ padding: '0.75rem', border: `1px solid ${theme.border}`, borderRadius: '10px', fontSize: 16, boxSizing: 'border-box', width: '100%', background: theme.surface, color: theme.text, outline: 'none' }}
                />
                <input
                  type="text"
                  placeholder="Artist (optional)"
                  value={suggestionArtist}
                  maxLength={200}
                  onChange={e => setSuggestionArtist(e.target.value)}
                  className="gr-input"
                  style={{ padding: '0.75rem', border: `1px solid ${theme.border}`, borderRadius: '10px', fontSize: 16, boxSizing: 'border-box', width: '100%', background: theme.surface, color: theme.text, outline: 'none' }}
                />
                <button
                  onClick={handleSuggest}
                  disabled={!suggestionTitle.trim()}
                  style={{
                    marginTop: 4,
                    padding: '15px',
                    background: suggestionTitle.trim() ? primary : theme.surfaceAlt,
                    color: suggestionTitle.trim() ? theme.onPrimary : theme.textMuted,
                    border: 'none', borderRadius: '12px',
                    fontWeight: 700, fontSize: 16,
                    cursor: suggestionTitle.trim() ? 'pointer' : 'not-allowed',
                    minHeight: 52,
                    WebkitTapHighlightColor: 'transparent',
                  }}
                >
                  Submit suggestion
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* ── FILTER BOTTOM SHEET ──────────────────────────── */}
      {filterOpen && (
        <>
          <div
            onClick={() => setFilterOpen(false)}
            style={{
              position: 'fixed', inset: 0,
              background: 'rgba(0,0,0,0.6)',
              zIndex: 200,
              WebkitTapHighlightColor: 'transparent',
            }}
          />
          <div
            ref={sheetRef}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            style={{
              position: 'fixed',
              bottom: 0, left: 0, right: 0,
              zIndex: 201,
              background: theme.surface,
              borderTop: `2px solid ${primary}`,
              borderRadius: '20px 20px 0 0',
              padding: `0 16px calc(20px + env(safe-area-inset-bottom))`,
              transform: `translateY(${sheetY}px)`,
              transition: sheetY === 0 ? 'transform 0.3s cubic-bezier(0.32,0.72,0,1)' : 'none',
              boxShadow: '0 -8px 40px rgba(0,0,0,0.3)',
              maxWidth: 600,
              margin: '0 auto',
            }}
          >
            {/* Drag handle */}
            <div style={{
              width: 40, height: 4,
              background: theme.border,
              borderRadius: 2,
              margin: '12px auto 20px',
            }} />

            {/* Title row */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
              <div>
                <div style={{ color: theme.text, fontWeight: 700, fontSize: 18 }}>Filter by Genre</div>
                <div style={{ color: theme.textMuted, fontSize: 12, marginTop: 3 }}>
                  {isAll
                    ? 'All genres shown'
                    : `${filteredSongs.length} song${filteredSongs.length !== 1 ? 's' : ''} match`
                  }
                </div>
              </div>
              <button
                onClick={() => setFilterOpen(false)}
                style={{
                  background: theme.surfaceAlt, border: 'none',
                  color: theme.textSecondary, borderRadius: '50%',
                  width: 34, height: 34,
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                <X size={18} />
              </button>
            </div>

            {/* Genre pills */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 18 }}>
              {['All', ...uniqueGenres].map(g => (
                <button
                  key={g}
                  onClick={() => toggleGenre(g)}
                  style={{
                    background: selectedGenres.includes(g) ? primary : theme.surfaceAlt,
                    color: selectedGenres.includes(g) ? theme.onPrimary : theme.textSecondary,
                    border: selectedGenres.includes(g) ? 'none' : `1px solid ${theme.border}`,
                    borderRadius: 24,
                    padding: '10px 20px',
                    fontSize: 15,
                    fontWeight: selectedGenres.includes(g) ? 700 : 400,
                    cursor: 'pointer',
                    WebkitTapHighlightColor: 'transparent',
                    minHeight: 44,
                    transition: 'background 0.15s, color 0.15s',
                  }}
                >
                  {g}
                </button>
              ))}
            </div>

            {/* Apply */}
            <button
              onClick={() => setFilterOpen(false)}
              style={{
                marginTop: 24,
                width: '100%',
                background: primary,
                color: theme.onPrimary,
                border: 'none',
                borderRadius: 12,
                padding: '15px',
                fontWeight: 700,
                fontSize: 16,
                cursor: 'pointer',
                minHeight: 52,
                WebkitTapHighlightColor: 'transparent',
                letterSpacing: 0.3,
              }}
            >
              {isAll ? 'Show All Songs' : `Show ${filteredSongs.length} Result${filteredSongs.length !== 1 ? 's' : ''}`}
            </button>
          </div>
        </>
      )}
    </>
  );
}

function PostGigSurveyModal({ theme, bandName, venueName, rating, setRating, favourite, setFavourite, leastFavourite, setLeastFavourite, onSubmit, onSkip, submitting, thanks }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1100,
      background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '1.5rem',
      fontFamily: 'system-ui, -apple-system, sans-serif',
    }}>
      <div style={{
        maxWidth: 420, width: '100%',
        background: theme.surface, color: theme.text,
        borderRadius: 16, padding: '1.75rem',
        position: 'relative',
        animation: 'gr-fade 0.25s ease',
      }}>
        <button onClick={onSkip} aria-label="Close"
          style={{ position: 'absolute', top: 12, right: 12, background: 'none', border: 'none', cursor: 'pointer', color: theme.textMuted, display: 'flex' }}>
          <X size={20} />
        </button>

        {thanks ? (
          <div style={{ textAlign: 'center', padding: '1.75rem 0' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>🎉</div>
            <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>Thanks for the feedback!</div>
          </div>
        ) : (
          <>
            <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.3rem' }}>Thanks for coming!</h2>
            <p style={{ margin: '0 0 1.25rem', color: theme.textSecondary, fontSize: '0.9rem' }}>
              {bandName}{venueName ? ` @ ${venueName}` : ''} — how was it?
            </p>

            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: theme.textSecondary, marginBottom: '0.5rem' }}>
                How would you rate tonight?
              </label>
              <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'center' }}>
                {[1, 2, 3, 4, 5].map(n => (
                  <button key={n} onClick={() => setRating(n)} aria-label={`${n} star${n !== 1 ? 's' : ''}`}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '2rem', padding: '0.15rem', lineHeight: 1, opacity: n <= rating ? 1 : 0.3, WebkitTapHighlightColor: 'transparent' }}>
                    ⭐
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: '0.85rem' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: theme.textSecondary, marginBottom: '0.3rem' }}>
                Favourite song tonight? <span style={{ fontWeight: 400 }}>(optional)</span>
              </label>
              <input
                className="gr-input"
                value={favourite}
                maxLength={200}
                onChange={e => setFavourite(e.target.value)}
                placeholder="e.g. Mr Brightside"
                style={{ width: '100%', boxSizing: 'border-box', padding: '0.65rem 0.75rem', border: `1px solid ${theme.border}`, borderRadius: 8, background: theme.background, color: theme.text, fontSize: 16 }}
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: theme.textSecondary, marginBottom: '0.3rem' }}>
                Least favourite? <span style={{ fontWeight: 400 }}>(optional)</span>
              </label>
              <input
                className="gr-input"
                value={leastFavourite}
                maxLength={200}
                onChange={e => setLeastFavourite(e.target.value)}
                placeholder="Totally optional"
                style={{ width: '100%', boxSizing: 'border-box', padding: '0.65rem 0.75rem', border: `1px solid ${theme.border}`, borderRadius: 8, background: theme.background, color: theme.text, fontSize: 16 }}
              />
            </div>

            <button
              onClick={onSubmit}
              disabled={!rating || submitting}
              style={{
                width: '100%', padding: '0.9rem',
                background: theme.primary, color: theme.onPrimary,
                border: 'none', borderRadius: 10,
                fontWeight: 700, fontSize: '1rem',
                cursor: rating && !submitting ? 'pointer' : 'not-allowed',
                opacity: rating ? 1 : 0.5,
                minHeight: 48,
              }}
            >
              {submitting ? 'Sending…' : 'Send Feedback'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default AudienceView;
